'use client'

import { ResponseCode } from '@marketplace-web/shared/api-client'

import { useTracking } from '@marketplace-web/shared/event-tracker'
import { useSession } from '@marketplace-web/shared/session'

import { useConversationContext } from '@marketplace-web/domain/core-conversation'
import {
  conversationReplyEvent,
  deleteConversationSuspicion,
  postReply,
  transformConversationResponse,
} from '@marketplace-web/domain/conversation-messaging-data'
import { UiState } from '@marketplace-web/shared/ui-state'

type Props = {
  value?: string | null
  photoTempUuids?: Array<string> | null
  isPersonalDataSharingCheckSkipped?: boolean
  onSetIsEmailSharingModalVisible?: (isVisible: boolean) => void
}

const usePostReply = () => {
  const { track } = useTracking()
  const { conversation, getConversationRequest, setConversation, setSendMessageUi } =
    useConversationContext()

  const userId = useSession().user?.id

  const postReplyRequest = async ({
    value,
    photoTempUuids,
    isPersonalDataSharingCheckSkipped = false,
    onSetIsEmailSharingModalVisible,
  }: Props) => {
    setSendMessageUi({ uiState: UiState.Pending })

    if (!conversation?.id || !userId) return

    const response = await postReply({
      conversationId: conversation.id,
      body: value,
      photoTempUuids,
      isPersonalDataSharingCheckSkipped,
    })

    if (response?.code === ResponseCode.EmailSharingDetected && 'errors' in response)
      onSetIsEmailSharingModalVisible?.(true)

    if ('errors' in response) {
      setSendMessageUi({ uiState: UiState.Failure, error: response.errors?.[0]?.value })

      return
    }

    if (conversation.isSuspicious) await deleteConversationSuspicion(conversation.id)

    setSendMessageUi({ uiState: UiState.Success })
    getConversationRequest({
      conversationId: conversation.id,
      fromPostReply: true,
    })

    if ('conversation' in response)
      setConversation(transformConversationResponse(response.conversation))

    track(conversationReplyEvent({ userId, itemId: conversation.itemId ?? null }))
  }

  return { postReplyRequest }
}

export default usePostReply
